/**
 * Sandboxes v2 detail page (`/sandboxes/v2/$sandboxId`): one claimed sandbox's
 * status, ways in (URLs, logins), seed outputs, actions and run history.
 *
 * @module features/sandboxes/pages/SandboxV2DetailPage
 */
import React from 'react';
import {
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  LoadingOrbit,
  Page,
  PageHeader,
  Text,
  useToast,
} from '@chatsdet/ui';
import { Trash } from '@phosphor-icons/react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useAuth } from '../../../contexts/AuthContext';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { SandboxActionsSection } from '../components/v2/detail/SandboxActionsSection';
import { SandboxLoginsSection, SandboxUrlsSection } from '../components/v2/detail/SandboxAccessSections';
import { SandboxSeedOutputsSection, SandboxTimingsSection } from '../components/v2/detail/SandboxFactsSections';
import { SandboxStatusChip, shortVersion } from '../components/v2/sandboxV2Display';
import { formatRelative } from '../components/overview/time-format';
import { useSandboxV2Detail } from '../hooks/useSandboxV2Detail';
import { useDestroySandboxV2 } from '../hooks/useSandboxesV2';

export const SandboxV2DetailPage: React.FC = () => {
  const { sandboxId } = useParams({ from: '/sandboxes/v2/$sandboxId' });
  return <SandboxV2DetailView key={sandboxId} sandboxId={sandboxId} />;
};

const SandboxV2DetailView: React.FC<{ sandboxId: string }> = ({ sandboxId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const enabled = Boolean(user?.features?.sandboxV2);

  React.useEffect(() => {
    if (user && !user.features?.sandboxV2) void navigate({ to: '/' });
  }, [user, navigate]);

  const query = useSandboxV2Detail(sandboxId, enabled);
  const destroy = useDestroySandboxV2();
  const sandbox = query.data;
  const applicationName = sandbox?.applicationName ?? 'Sandbox';

  useBreadcrumbs([
    { key: 'sandboxes', label: 'Test Environments', onPress: () => navigate({ to: '/sandboxes', search: { view: 'v2' } }) },
    { key: `sandbox-v2-${sandboxId}`, label: sandbox ? applicationName : 'Loading...' },
  ]);

  const handleDestroy = () => {
    destroy.mutate(sandboxId, {
      onSuccess: () => {
        toast.success('Sandbox is being destroyed');
        setConfirmOpen(false);
        void navigate({ to: '/sandboxes', search: { view: 'v2' } });
      },
      onError: (error) => toast.error(error.message),
    });
  };

  if (!enabled) return null;
  if (query.isLoading) {
    return (
      <Page>
        <Flex justify="center" className="py-24">
          <LoadingOrbit size="lg" />
        </Flex>
      </Page>
    );
  }
  if (query.isError || !sandbox) {
    return (
      <Page>
        <Text color="muted">{query.error?.message ?? 'Sandbox not found'}</Text>
      </Page>
    );
  }

  const expiry = sandbox.leaseExpiresAt
    ? sandbox.expired
      ? `Expired ${formatRelative(sandbox.leaseExpiresAt)}`
      : `Expires ${formatRelative(sandbox.leaseExpiresAt)}`
    : null;

  return (
    <Page>
      <PageHeader
        title={applicationName}
        eyebrow={<SandboxStatusChip status={sandbox.status} error={sandbox.error} />}
        description={
          <span className="flex flex-wrap items-center gap-x-3">
            <span>{sandbox.environment}</span>
            <span className="font-mono">{shortVersion(sandbox.version)}</span>
            {expiry && <span>{expiry}</span>}
          </span>
        }
        actions={
          sandbox.status === 'destroyed' ? null : (
            <Button variant="destructive" size="sm" onPress={() => setConfirmOpen(true)}>
              <Trash size={14} />
              Destroy
            </Button>
          )
        }
      />
      <Flex direction="column" gap="lg" className="mt-4 max-w-4xl">
        {sandbox.status === 'claiming' && (
          <Flex align="center" gap="sm" className="rounded-md bg-muted px-4 py-3" role="status">
            <LoadingOrbit size="sm" />
            <Text>Claiming a sandbox. Copying the snapshot and preparing logins; this page updates on its own.</Text>
          </Flex>
        )}
        {sandbox.status === 'failed' && (
          <Banner variant="error" className="flex-col !items-stretch">
            <Text weight="semibold">The claim failed</Text>
            <pre className="max-h-64 overflow-auto font-mono text-xs whitespace-pre-wrap break-all">
              {sandbox.error ?? 'No error was recorded.'}
            </pre>
          </Banner>
        )}
        <SandboxTimingsSection sandbox={sandbox} />
        {sandbox.status === 'ready' && (
          <>
            <SandboxUrlsSection portUrls={sandbox.portUrls} />
            <SandboxLoginsSection personas={sandbox.personas} />
            <SandboxSeedOutputsSection seedOutputs={sandbox.seedOutputs} />
            <SandboxActionsSection sandbox={sandbox} />
          </>
        )}
      </Flex>

      <DialogTrigger isOpen={confirmOpen} onOpenChange={setConfirmOpen} size="sm">
        <span />
        <Dialog title="Destroy sandbox" description="Are you sure you want to destroy this sandbox?">
          <DialogContent>
            <Text>
              This tears down the sandbox ({applicationName} / {sandbox.environment}) and discards its data. This cannot
              be undone.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setConfirmOpen(false)} isDisabled={destroy.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onPress={handleDestroy} isDisabled={destroy.isPending}>
              {destroy.isPending ? 'Destroying…' : 'Destroy'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Page>
  );
};
